import { useCallback, useEffect, useRef, useState } from "react";
import { ASSIGNMENT_POLL_MS, POLL_MS, YOUR_TURN_MS } from "../config";
import { toActivity } from "../data/activityAdapter";
import { findAttempt, getOrCreateAttempt, submitStep } from "../data/learnerService";
import { DENIED, SETUP, UNAVAILABLE, UNSUPPORTED, problemOf } from "../data/problems";
import { clearAllRuntime } from "../data/runtimeStore";
import { resolvePolicy } from "../data/shared/responseEvaluation";
import useAttemptSteps from "./useAttemptSteps";

// Waiting → Your turn → steps → Complete (Handoff 001 §9), plus the learner's
// own work outside a lesson (Handoff 007 §10):
//
//   Current work  the live release first; then open homework, practice and
//                 assessment, listed on the waiting screen
//   My Learning   completed work the learner really did, to look back at or
//                 practise again without saving anything
//
// phase: "checking" | "waiting" | "your-turn" | "activity" | "complete"
//      | "assignment-done" | "my-learning" | "revision"
//      | "signed-out" | "not-ready"
//
// "signed-out" means this tablet has not been set up (or was reset).
// `reason` explains "not-ready" to the adult: "not-enrolled", "no-access"
// (this tablet may not read the learner's class or work), "release-unavailable"
// (the release state itself could not be read, which is a setup problem rather
// than an account problem) or "unreadable" (the activity or a record did not
// match the expected shape) or "unsupported-device" (the tablet's browser is
// too old for this activity).
// While a save is in flight, `saving` is true; if it fails, `retry` holds the
// action to try again and the screen stays where it is.

// Directus answers "forbidden" both when an account may not read something and
// when what is being read does not exist, so a refused read is explained by
// which read it was rather than by the error alone.
const from = (source, work) =>
  work.catch((error) => {
    if (error && typeof error === "object" && !error.source) error.source = source;
    throw error;
  });

const DENIED_REASON = {
  release: "release-unavailable",
};

// The attempt stores the step's position. If it is missing or no longer fits
// the activity, the saved responses say where the learner had got to.
const stepIndexOf = (activity, attempt) => {
  const saved = attempt.currentStep;
  if (Number.isInteger(saved) && saved >= 0 && saved < activity.steps.length)
    return saved;
  const unanswered = activity.steps.findIndex((s) => !attempt.responses[s.id]);
  return unanswered >= 0 ? unanswered : activity.steps.length - 1;
};

// The gateway removes expected answers whenever the effective feedback policy
// does not yet permit them; the adapter is told so explicitly.
const liveActivity = (record) => {
  const policy = resolvePolicy(record?.activity_definition, "live");
  return toActivity(record, { answersWithheld: policy?.feedbackTiming !== "immediate" });
};
const assignmentActivity = ({ assignment, activity }) =>
  toActivity(activity, {
    answersWithheld: assignment?.policy?.feedbackTiming !== "immediate",
  });

const activityState = (activity, attempt, source) => ({
  phase: "activity",
  source,
  activity,
  attempt,
  stepIndex: stepIndexOf(activity, attempt),
  verdict: null,
  runtime: {},
});

const finish = (s) =>
  s.source?.kind === "assignment"
    ? { phase: "assignment-done", assignment: s.source.assignment }
    : { phase: "complete" };

const useLearnerSession = (repo) => {
  const [state, setState] = useState({ phase: "checking" });
  const [saving, setSaving] = useState(false);
  const [retry, setRetry] = useState(null);
  const entering = useRef(false);
  // Whether this tablet is set up is asked once, before its first read.
  const setUp = useRef(false);

  const settleProblem = useCallback(
    async (error, fallbackPhase) => {
      const problem = problemOf(error);
      if (problem === DENIED) {
        setState(
          (await repo.isSignedIn())
            ? {
                phase: "not-ready",
                reason: DENIED_REASON[error?.source] || "no-access",
              }
            : { phase: "signed-out" }
        );
      } else if (problem === SETUP) {
        setState({ phase: "not-ready", reason: "release-unavailable" });
      } else if (problem === UNSUPPORTED) {
        setState({ phase: "not-ready", reason: "unsupported-device" });
      } else if (problem === UNAVAILABLE) {
        setState({ phase: "not-ready", reason: "unreadable" });
      } else {
        setState((s) => (s.phase === "checking" ? { phase: fallbackPhase } : s));
      }
    },
    [repo]
  );

  // Homework is extra: if it cannot be read, the waiting screen still works.
  const readAssignments = useCallback(async () => {
    if (!repo.listAssignments) return [];
    try {
      return await repo.listAssignments();
    } catch {
      return null;
    }
  }, [repo]);

  // Reads where the learner is. Creates nothing.
  const resolve = useCallback(async () => {
    try {
      if (!setUp.current) {
        if (!(await repo.isSignedIn())) {
          setState({ phase: "signed-out" });
          return;
        }
        setUp.current = true;
      }
      const enrolled = await from("enrolment", repo.isEnrolled());
      if (!enrolled) {
        setState({ phase: "not-ready", reason: "not-enrolled" });
        return;
      }
      const release = await from("release", repo.currentRelease());
      if (!release) {
        const assignments = await readAssignments();
        setState({ phase: "waiting", assignments });
        return;
      }
      const activity = liveActivity(
        await from("activity", repo.readActivity(release.activityId))
      );
      const attempt = await from("work", findAttempt(repo, { release }));
      const source = { kind: "live", release };
      if (attempt?.completed) setState({ phase: "complete" });
      else if (attempt) setState(activityState(activity, attempt, source));
      else setState({ phase: "your-turn", release, activity });
    } catch (error) {
      settleProblem(error, "waiting");
    }
  }, [repo, settleProblem, readAssignments]);

  useEffect(() => {
    resolve();
  }, [resolve]);

  // Simple polling while waiting for the teacher. A failed check just waits
  // for the next one. Homework is refreshed far less often.
  const phase = state.phase;
  useEffect(() => {
    if (phase !== "waiting") return undefined;
    const release = setInterval(async () => {
      try {
        if (await repo.currentRelease()) resolve();
      } catch {
        // The next check will try again.
      }
    }, POLL_MS);
    const homework = setInterval(async () => {
      const assignments = await readAssignments();
      if (assignments) {
        setState((s) => (s.phase === "waiting" ? { ...s, assignments } : s));
      }
    }, ASSIGNMENT_POLL_MS);
    return () => {
      clearInterval(release);
      clearInterval(homework);
    };
  }, [phase, repo, resolve, readAssignments]);

  // Runs a save; moves on only once the server has confirmed it.
  const run = useCallback(
    async (action) => {
      setSaving(true);
      setRetry(null);
      try {
        await action();
      } catch (error) {
        // The work moved on elsewhere (a second tablet tab, an attempt that is
        // already finished): read afresh instead of offering a retry that
        // cannot succeed.
        if (error?.response?.status === 409) resolve();
        else setRetry(() => () => run(action));
      } finally {
        setSaving(false);
      }
    },
    [resolve]
  );

  // The attempt is created as the learner enters the first interaction, not
  // at release (handoff §11).
  const enter = useCallback(() => {
    if (entering.current || state.phase !== "your-turn") return;
    entering.current = true;
    const { release, activity } = state;
    run(async () => {
      try {
        const attempt = await getOrCreateAttempt(repo, { release });
        setState(
          attempt.completed
            ? { phase: "complete" }
            : activityState(activity, attempt, { kind: "live", release })
        );
      } finally {
        entering.current = false;
      }
    });
  }, [state, repo, run]);

  // The hand-off cue advances itself. The timer changes the UI into the first
  // interaction; attempt creation belongs to that transition and never to
  // Waiting, release discovery, or the time the cue remains visible.
  useEffect(() => {
    if (state.phase !== "your-turn") return undefined;
    const timer = setTimeout(enter, YOUR_TURN_MS);
    return () => clearTimeout(timer);
  }, [state.phase, enter]);

  const submit = useCallback(
    (attempt, submission) => submitStep(repo, attempt, submission),
    [repo]
  );
  const { completeStep, saveProgress } = useAttemptSteps({
    state,
    setState,
    submit,
    run,
    finish,
  });

  // Opening homework is the learner's first interaction with it, so the one
  // deterministic attempt is created (or resumed) here.
  const openAssignment = useCallback(
    (assignmentId) => {
      run(async () => {
        let read;
        try {
          read = await repo.readAssignment(assignmentId);
        } catch (error) {
          // Closed, past its date or no longer for this learner: back to the
          // list, read afresh.
          if (error?.response?.status === 404) {
            await resolve();
            return;
          }
          throw error;
        }
        let activity;
        try {
          activity = assignmentActivity(read);
        } catch (error) {
          await settleProblem(error, "waiting");
          return;
        }
        const attempt = await repo.startAssignment(assignmentId);
        setState(
          attempt.completed
            ? { phase: "assignment-done", assignment: read.assignment }
            : activityState(activity, attempt, {
                kind: "assignment",
                assignment: read.assignment,
              })
        );
      });
    },
    [repo, run, resolve, settleProblem]
  );

  const openMyLearning = useCallback(() => {
    run(async () => {
      const items = await repo.myLearning();
      setState({ phase: "my-learning", items });
    });
  }, [repo, run]);

  // A finished attempt, rebuilt from what was saved. Nothing is written.
  const openRevision = useCallback(
    (attemptId) => {
      run(async () => {
        const { attempt, activity } = await repo.readRevision(attemptId);
        // The gateway marks a step "submitted" (and drops every outcome and
        // expected answer) exactly when feedback is still withheld.
        const withheld = Object.values(attempt.responses || {}).some(
          (record) => record?.state === "submitted"
        );
        let adapted = null;
        try {
          adapted = toActivity(activity, { answersWithheld: withheld });
        } catch {
          // Shown as "can't be shown again here" rather than an error.
        }
        setState((s) => ({
          phase: "revision",
          items: s.items,
          attempt,
          activity: adapted,
          title: activity?.title || null,
        }));
      });
    },
    [repo, run]
  );

  // Leaving homework part-way keeps it: the same attempt resumes next time.
  const goHome = useCallback(() => {
    setRetry(null);
    setState({ phase: "checking" });
    resolve();
  }, [resolve]);

  // A part the activity cannot do without (a tracing template) could not be
  // shown. The adult sees the calm "can't be shown" message; Check again reads
  // everything afresh and resumes the same attempt.
  const unavailable = useCallback(
    () => setState({ phase: "not-ready", reason: "unreadable" }),
    []
  );

  return {
    ...state,
    saving,
    retry,
    unavailable,
    recheck: resolve,
    signOut: async () => {
      await repo.signOut();
      clearAllRuntime();
      setUp.current = false;
      setState({ phase: "signed-out" });
    },
    completeStep,
    saveProgress,
    openAssignment,
    openMyLearning,
    openRevision,
    goHome,
  };
};

export default useLearnerSession;
